import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../../design/Icon';
import { Card } from '../../components/shared/Card';
import { useToast } from '../../components/shared/Overlay';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { scopeLabel, useShellStatus, useTerms } from '../../layout/ShellStatus';
import { reportsAPI } from '../../services/reportsApi';
import { barQuery, exportedWhen, exportExtension, recentExportTitle, rerunRequest } from './reportsLogic';

// Quick-export rows in the mockup's order and words; a row shows only when GET reports/datasets lists the dataset.
const QUICK_EXPORTS = [
  { dataset: 'Calls', title: 'Calls', description: (terms) => `One row per call with route, ${terms.communityLower}, classifications, sentiment, language` },
  { dataset: 'CallJourneyHops', title: 'Call route segments', description: 'One row per segment: auto attendant, queue, agent, transfer, voicemail' },
  { dataset: 'Classifications', title: 'Classifications', description: 'One row per classification per call, with severity, confidence, and evidence' },
  { dataset: 'Agents', title: 'Agents', description: 'Agent performance for the period' },
  { dataset: 'Queues', title: 'Queues', description: 'Queue performance and SLA' },
  { dataset: 'ServiceLevel', title: 'SLA by day', description: 'Daily SLA by queue' },
  { dataset: 'Transcripts', title: 'Transcripts', description: 'Diarized transcript text, redacted, one file per call (.zip of .txt)', zip: true },
];

const RECENT_SHOWN = 6;
const integer = (n) => Number(n).toLocaleString('en-US');

async function rerun(record) {
  const request = rerunRequest(record);
  if (request.action === 'saved') return reportsAPI.runSaved(request.id, request.format);
  if (request.action === 'community') return reportsAPI.communitySummary(request.params);
  if (request.action === 'transcripts') return reportsAPI.exportTranscripts(request.params);
  if (request.action === 'page') return reportsAPI.pageExport(request.route, request.params);
  return reportsAPI.run(request.query, request.format);
}

export function ExportsTab({ datasets }) {
  const toast = useToast();
  const hierarchy = useHierarchyFilter();
  const terms = useTerms();
  const { params, range } = hierarchy;
  const { status: shellStatus } = useShellStatus();
  const [counts, setCounts] = useState({});
  const [recent, setRecent] = useState(null);
  const [busy, setBusy] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const available = useMemo(() => {
    const listed = new Set((datasets || []).map((d) => d.dataset));
    return QUICK_EXPORTS.filter((q) => listed.has(q.dataset));
  }, [datasets]);

  const loadRecent = useCallback(() => reportsAPI.getRecentExports().then(setRecent, () => setRecent([])), []);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  useEffect(() => {
    let cancelled = false;
    setCounts({});
    const query = barQuery(params);
    available.forEach((row) => {
      reportsAPI
        .preview({ dataset: row.dataset, columns: [], ...query, limit: 0 })
        .then((preview) => !cancelled && setCounts((c) => ({ ...c, [row.dataset]: preview.totalRows })))
        .catch(() => !cancelled && setCounts((c) => ({ ...c, [row.dataset]: null })));
    });
    return () => {
      cancelled = true;
    };
  }, [available, params]);

  const finish = (result) => {
    if (result.success) {
      toast(`Exported ${result.filename}`);
      loadRecent();
    } else {
      toast(result.error, 'alert');
    }
  };

  const quickExport = async (row, format) => {
    setBusy(`${row.dataset}-${format}`);
    const result = row.zip ? await reportsAPI.exportTranscripts(params) : await reportsAPI.exportDataset(row.dataset, params, format);
    setBusy(null);
    finish(result);
  };

  const download = async (record) => {
    setBusy(record.id);
    const result = await rerun(record);
    setBusy(null);
    finish(result);
  };

  return (
    <div className="grid">
      <Card className="c-7" title="Quick export" description={`Uses the filters in the bar above: ${scopeLabel(hierarchy, shellStatus)}, ${range.rangeLabel}`} flush>
        <div className="table-wrap">
          <table className="table">
            <tbody>
              {available.map((row) => {
                const count = counts[row.dataset];
                const unit = row.zip ? 'files' : 'rows';
                return (
                  <tr key={row.dataset}>
                    <td>
                      <div className="cell-title">{row.title}</div>
                      <div className="cell-sub">{typeof row.description === 'function' ? row.description(terms) : row.description}</div>
                    </td>
                    <td className="caption nowrap">{count === undefined ? '…' : count === null ? '—' : `${integer(count)} ${unit}`}</td>
                    <td className="nowrap" style={{ textAlign: 'right' }}>
                      {row.zip ? (
                        <button type="button" className="btn btn-sm" disabled={busy === `${row.dataset}-zip`} onClick={() => quickExport(row, 'zip')}>
                          <Icon name="download" className="ico-sm" />
                          .zip
                        </button>
                      ) : (
                        <>
                          <button type="button" className="btn btn-sm" disabled={busy === `${row.dataset}-csv`} onClick={() => quickExport(row, 'csv')}>
                            <Icon name="download" className="ico-sm" />
                            .csv
                          </button>{' '}
                          <button type="button" className="btn btn-sm" disabled={busy === `${row.dataset}-xlsx`} onClick={() => quickExport(row, 'xlsx')}>
                            <Icon name="download" className="ico-sm" />
                            .xlsx
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <Card className="c-5" title="Recent exports" description="Re-run any export from the last 7 days" flush loading={recent === null} data-testid="recent-exports">
        {recent && recent.length === 0 ? (
          <p className="caption" style={{ padding: '12px 16px', borderTop: '1px solid var(--divider)', margin: 0 }}>
            Nothing exported in the last 7 days.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {(recent || []).slice(0, showAll ? undefined : RECENT_SHOWN).map((record) => (
              <li key={record.id} className="row" style={{ padding: '12px 16px', borderTop: '1px solid var(--divider)', gap: 12 }}>
                <span style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--surface-3)', display: 'grid', placeItems: 'center', flex: 'none' }}>
                  <Icon name="file" />
                </span>
                <div className="grow">
                  <div className="small strong">
                    {recentExportTitle(record)}{' '}
                    <span className="muted" style={{ fontWeight: 400 }}>
                      {exportExtension(record)}
                    </span>
                  </div>
                  <div className="caption">
                    {[record.userName, exportedWhen(record.createdAt), record.rowCount != null ? `${integer(record.rowCount)} ${record.kind === 'TranscriptArchive' ? 'files' : 'rows'}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
                {rerunRequest(record) ? (
                  <button type="button" className="btn btn-sm" disabled={busy === record.id} onClick={() => download(record)}>
                    <Icon name="download" className="ico-sm" />
                    Download
                  </button>
                ) : (
                  <span className="badge" title={record.kind === 'LegacyExport' ? 'This export route was retired; export the dataset again' : 'Export this table again from its page'}>
                    {record.kind === 'LegacyExport' ? 'Retired' : 'From its page'}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {recent && recent.length > RECENT_SHOWN && (
          <div style={{ padding: '8px 16px', borderTop: '1px solid var(--divider)' }}>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show fewer' : `Show all ${recent.length}`}
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}
